import { calendarDates, moveMonth } from './calendarDates';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/Button';
import { getAvailabilityMonth } from '../../api/client';
import { useI18n } from '../../i18n/I18nProvider';

export function AvailabilityCalendar({ initial, min, max, timezone, artistId, locationId, onContinue }: {
  initial: string; min: string; max: string; timezone?: string; artistId: string | null; locationId: string | null; onContinue: (date: string) => void;
}) {
  const { t, locale } = useI18n();
  const [month, setMonth] = useState((initial >= min && initial <= max ? initial : min).slice(0, 7));
  const [selected, setSelected] = useState(initial);
  const [days, setDays] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [zone, setZone] = useState(timezone || '');
  const grid = useRef<HTMLDivElement>(null);
  const restoreGridFocus = useRef(false);
  const [focused, setFocused] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setDays({});
    void getAvailabilityMonth(month, artistId, locationId).then((value) => {
      if (!active) return;
      setDays(value.days); setZone(value.timezone);
      setFocused((current) => value.days[current] ? current : Object.keys(value.days).find((date) => value.days[date] && date >= min && date <= max) || '');
      // Warm adjacent pages only after the current month is interactive. The
      // shared API cache coalesces this with a quick user navigation.
      for (const adjacent of [moveMonth(month, -1), moveMonth(month, 1)]) {
        if (adjacent >= min.slice(0, 7) && adjacent <= max.slice(0, 7)) {
          void getAvailabilityMonth(adjacent, artistId, locationId).catch(() => undefined);
        }
      }
    }).catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : t('api.requestFailed')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [artistId, locationId, max, min, month, attempt, t]);
  useEffect(() => {
    if (!loading && restoreGridFocus.current) {
      grid.current?.querySelector<HTMLButtonElement>('[tabindex="0"]')?.focus();
      restoreGridFocus.current = false;
    }
  }, [loading, focused]);
  const monthLabel = new Date(`${month}-01T12:00:00Z`).toLocaleDateString(locale, { month: 'long', timeZone: 'UTC' });
  const cells = calendarDates(month);
  const canSelect = (date: string) => !loading && !error && date >= min && date <= max && Boolean(days[date]);
  const changeMonth = (amount: number, focusGrid = false) => {
    const next = moveMonth(month, amount);
    if (next < min.slice(0, 7) || next > max.slice(0, 7)) return;
    restoreGridFocus.current = focusGrid;
    setMonth(next);
  };
  const keydown = (event: KeyboardEvent<HTMLButtonElement>, date: string) => {
    if (event.key === 'PageUp' || event.key === 'PageDown') { event.preventDefault(); changeMonth(event.key === 'PageUp' ? -1 : 1, true); return; }
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    const current = cells.indexOf(date);
    let next = current;
    if (step) { next += step; while (next >= 0 && next < cells.length && (!cells[next] || !canSelect(cells[next]!))) next += step; }
    else if (event.key === 'Home' || event.key === 'End') {
      const week = Math.floor(current / 7) * 7;
      const candidates = cells.slice(week, week + 7).filter((day): day is string => Boolean(day && canSelect(day)));
      next = cells.indexOf((event.key === 'Home' ? candidates[0] : candidates.at(-1)) || date);
    } else return;
    event.preventDefault();
    const target = cells[next];
    if (target && canSelect(target)) { setFocused(target); grid.current?.querySelector<HTMLButtonElement>(`[data-date="${target}"]`)?.focus(); }
  };
  return <>
    <section className="availability-calendar" aria-label={t('calendar.label')} aria-busy={loading}>
      <header><button type="button" className="icon-button" onClick={() => changeMonth(-1)} disabled={month <= min.slice(0, 7)} aria-label={t('calendar.previous')}><ChevronLeft /></button><h2 aria-live="polite">{monthLabel}</h2><button type="button" className="icon-button" onClick={() => changeMonth(1)} disabled={month >= max.slice(0, 7)} aria-label={t('calendar.next')}><ChevronRight /></button></header>
      <div ref={grid} role="grid" aria-label={monthLabel} className="calendar-grid">
        <div role="row" className="calendar-weekdays">{Array.from({ length: 7 }, (_, index) => <span role="columnheader" key={index}>{new Date(Date.UTC(2026, 0, 5 + index, 12)).toLocaleDateString(locale, { weekday: 'short', timeZone: 'UTC' })}</span>)}</div>
        {Array.from({ length: cells.length / 7 }, (_, week) => <div role="row" className="calendar-week" key={week}>{cells.slice(week * 7, week * 7 + 7).map((date, index) => <div role="gridcell" aria-selected={date ? date === selected : undefined} key={date || `empty-${index}`}>{date && <button type="button" data-date={date} disabled={!canSelect(date)} tabIndex={date === focused && canSelect(date) ? 0 : -1} aria-label={`${new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, { dateStyle: 'full', timeZone: 'UTC' })}${canSelect(date) ? '' : ` · ${t('calendar.unavailable')}`}`} aria-current={date === min ? 'date' : undefined} className={date === selected ? 'is-selected' : ''} onFocus={() => setFocused(date)} onKeyDown={(event) => keydown(event, date)} onClick={() => setSelected(date)}>{Number(date.slice(8))}</button>}</div>)}</div>)}
      </div>
      <p className="calendar-status" role="status">{loading ? t('calendar.loading') : t('calendar.timezone', { timezone: zone })}</p>
      {!loading && !error && !Object.keys(days).some(canSelect) && <p className="panel-note">{t('calendar.noDays')}</p>}
      {error && <div role="alert"><p className="inline-error">{error}</p><Button variant="secondary" onClick={() => setAttempt((current) => current + 1)}>{t('common.retry')}</Button></div>}
    </section>
    <div className="sticky-cta"><Button block disabled={!canSelect(selected)} onClick={() => canSelect(selected) && onContinue(selected)}>{t('common.continue')}</Button></div>
  </>;
}
